import { useQuery } from '@tanstack/react-query';

import type { ApiError } from '@/types/api.types';

import { getSystemChartTemplate, listSystemChartTemplates } from '../api/chartTemplatesApi';
import type {
  ChartTemplateDetail,
  ChartTemplateListData,
  ListSystemChartTemplatesParams,
} from '../types';

const systemChartTemplatesKey = (params: ListSystemChartTemplatesParams) =>
  ['chartTemplates', 'system', params] as const;

const systemChartTemplateKey = (templateId: string) =>
  ['chartTemplates', 'system', 'detail', templateId] as const;

/** Read-only list of system-level chart templates from the public catalog. */
export function useSystemChartTemplates(params: ListSystemChartTemplatesParams = {}) {
  const query = useQuery<ChartTemplateListData, ApiError>({
    queryKey: systemChartTemplatesKey(params),
    queryFn: () => listSystemChartTemplates(params),
  });

  return {
    templates: query.data?.items ?? [],
    pagination: query.data?.pagination ?? null,
    isLoading: query.isLoading,
    fetchError: query.error ?? null,
  };
}

/** A single system template with its full active version definition. */
export function useSystemChartTemplate(templateId: string | undefined) {
  const query = useQuery<ChartTemplateDetail, ApiError>({
    queryKey: systemChartTemplateKey(templateId ?? ''),
    queryFn: () => getSystemChartTemplate(templateId as string),
    enabled: !!templateId,
  });

  return {
    template: query.data ?? null,
    isLoading: query.isLoading,
    fetchError: query.error ?? null,
  };
}
